/*
 * The copilot's flag and base URL: the only module that reads
 * REACT_APP_COPILOT_*. Both are read at call time, like utils/env.js, so
 * tests can set them per test.
 *
 * REACT_APP_COPILOT_ENABLED and REACT_APP_COPILOT_URL hold no secret: the
 * copilot sends only the user's own MSAL token, fetched per call.
 */

import { isProductionEnvironment } from "../../utils/env";

/** REACT_APP_COPILOT_URL, trimmed, with any trailing "/" removed; "" when unset. */
export function getCopilotBaseUrl() {
  const raw = process.env.REACT_APP_COPILOT_URL;
  return typeof raw === "string" ? raw.trim().replace(/\/+$/, "") : "";
}

/**
 * On only when REACT_APP_COPILOT_ENABLED is exactly "true", the URL is set,
 * and the environment isn't Prod (DESIGN REQUEST 2). Empty or unset is off.
 */
export function isCopilotEnabled() {
  return (
    process.env.REACT_APP_COPILOT_ENABLED === "true" &&
    getCopilotBaseUrl() !== "" &&
    !isProductionEnvironment()
  );
}

const HTTPS_BASE = /^https:\/\/[^/?#@\s]+(\/[^?#\s]*)?$/i;
const LOCALHOST_BASE = /^http:\/\/localhost(:\d{1,5})?(\/[^?#\s]*)?$/i;

/**
 * An absolute https URL, or http://localhost for a developer's own host.
 * No credentials, query or fragment.
 *
 * The text must match one of the two patterns, and the parsed URL is
 * checked again field by field, so a string the pattern lets through but
 * the browser reads differently is still refused.
 */
export function isSafeCopilotBaseUrl(url) {
  if (typeof url !== "string") return false;
  if (!HTTPS_BASE.test(url) && !LOCALHOST_BASE.test(url)) return false;
  let parsed;
  try {
    parsed = new URL(url);
  } catch (err) {
    return false;
  }
  if (parsed.username !== "" || parsed.password !== "") return false;
  if (parsed.search !== "" || parsed.hash !== "") return false;
  if (parsed.protocol === "https:") return parsed.hostname !== "";
  if (parsed.protocol === "http:") return parsed.hostname === "localhost";
  return false;
}

/**
 * The base URL the service and the stream may call, or null. Fails closed
 * (approval change 6): with the flag off, or a URL that isn't absolute
 * https or http://localhost, nothing is called, so the fresh token can
 * never go to a relative path (the sim's baseURL) or another scheme.
 */
export function getCopilotRequestBase() {
  if (!isCopilotEnabled()) return null;
  const base = getCopilotBaseUrl();
  return isSafeCopilotBaseUrl(base) ? base : null;
}

function hex(bytes) {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * A new id for a client_message_id or a draft id: crypto.randomUUID() in the
 * browser, else a v4 UUID from getRandomValues (jsdom 16 has neither, so
 * hooks take an injected generator and tests pass their own).
 */
export function defaultIdGenerator() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === "function") {
    return cryptoApi.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (cryptoApi && typeof cryptoApi.getRandomValues === "function") {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = hex(bytes);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
